import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import LoginView from '@/views/auth/LoginView.vue'
import TranslationManager from '@/views/admin/translations/TranslationManager.vue'
import AdminLayout from '@/views/admin/AdminLayout.vue'
import SessionTemplateManager from '@/views/admin/session-templates/SessionTemplateManager.vue'
import AdminResourceManager from '@/views/admin/resources/AdminResourceManager.vue'
import AdminTrainingManager from '@/views/admin/trainings/AdminTrainingManager.vue'
import AdminProgramManager from '@/views/admin/programs/AdminProgramManager.vue'
import AdminProgramCreationView from '@/views/admin/programs/AdminProgramCreationView.vue'
import EscoManager from '@/views/admin/esco/EscoManager.vue'
import EmailTemplatesManager from '@/views/admin/email-templates/EmailTemplatesManager.vue'
import PlatformSkillsManager from '@/views/admin/platform-skills/PlatformSkillsManager.vue'
import OccupationSkillsManager from '@/views/admin/occupation-skills/OccupationSkillsManager.vue'
import AiPromptManager from '@/views/admin/ai-prompts/AiPromptManager.vue'
import AdminBillingManager from '@/views/admin/billing/AdminBillingManager.vue'
import CatalogPricesManager from '@/views/admin/catalog/CatalogPricesManager.vue'
import NotificationManager from '@/views/admin/notifications/NotificationManager.vue'

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { guest: true }
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, requiresSuperAdmin: true },
    redirect: '/admin/translations',
    children: [
      {
        path: 'translations',
        name: 'admin.translations',
        component: TranslationManager,
      },
      {
        path: 'session-templates',
        name: 'admin.session-templates',
        component: SessionTemplateManager,
      },
      {
        path: 'resources',
        name: 'admin.resources',
        component: AdminResourceManager,
      },
      {
        path: 'trainings',
        name: 'admin.trainings',
        component: AdminTrainingManager,
      },
      {
        path: 'programs',
        name: 'admin.programs',
        component: AdminProgramManager,
      },
      {
        path: 'programs/create',
        name: 'admin.programs.create',
        component: AdminProgramCreationView,
      },
      {
        path: 'programs/:id/edit',
        name: 'admin.programs.edit',
        component: AdminProgramCreationView,
      },
      {
        path: 'esco',
        name: 'admin.esco',
        component: EscoManager,
      },
      {
        path: 'email-templates',
        name: 'admin.email-templates',
        component: EmailTemplatesManager,
      },
      {
        path: 'platform-skills',
        name: 'admin.platform-skills',
        component: PlatformSkillsManager,
      },
      {
        path: 'occupation-skills',
        name: 'admin.occupation-skills',
        component: OccupationSkillsManager,
      },
      {
        path: 'ai-prompts',
        name: 'admin.ai-prompts',
        component: AiPromptManager,
      },
      {
        path: 'billing',
        name: 'admin.billing',
        component: AdminBillingManager,
      },
      {
        path: 'catalog',
        name: 'admin.catalog',
        component: CatalogPricesManager,
      },
      {
        path: 'notifications',
        name: 'admin.notifications',
        component: NotificationManager,
      },
    ]
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/errors/403.vue')
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Si la route nécessite d'être connecté
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      return next({ name: 'login', query: { redirect: to.fullPath } })
    }

    // Charger les infos user si pas encore fait
    if (!authStore.user) {
      try {
        await authStore.fetchUser()
      } catch (error) {
        return next({ name: 'login' })
      }
    }

    // Vérifier super admin
    if (to.meta.requiresSuperAdmin && !authStore.isSuperAdmin) {
      return next({ name: 'forbidden' })
    }
  }

  // Si route guest (login) et déjà connecté
  if (to.meta.guest && authStore.isAuthenticated) {
    return next({ name: 'admin.translations' })
  }

  next()
})

export default router